import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { ConfigService } from '@docma-mm-core/config/config.service';
import { BasedataService } from '@docma-mm-core/shared/basedata.service';
import { AppContainerOptions, AppRightDrawerService, PendingService } from '@mydocma/app-container';
import { NotificationService } from '@mydocma/component-library/src/lib/notification';
import { Task, TaskFileDownloadService, TaskService, TaskStatusEnum } from '@mydocma/task';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { MmReportTask } from '../mm-report-task';
import { MmReportTypeEnum } from '../mm-report-type.enum';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';


@UntilDestroy()
@Injectable()
export class MmFaultsReportingService extends BasedataService implements OnDestroy {

  private fileDownloadSubject = new Subject<{task: Task, file: File}>();
  private finishSubject = new Subject<Task>();
  private reportReadySubject = new Subject<MmReportTask>();

  public onReportFileDownloaded = this.fileDownloadSubject.asObservable();
  public onReportTaskFinished = this.finishSubject.asObservable();
  public onReportReady = this.reportReadySubject.asObservable();

  private reportPendingState = new Map<string, BehaviorSubject<boolean>>();
  private registerdReportTasks = new Map<string, MmReportTask>();

  constructor(private http: HttpClient, configService: ConfigService,
              private readonly translateService: TranslateService,
              private readonly taskDownloadService: TaskFileDownloadService,
              private readonly taskService: TaskService,
              private readonly appRightDrawerService: AppRightDrawerService,
              private readonly appContainerOptions: AppContainerOptions,
              private readonly notificationService: NotificationService,
              private readonly pendingService: PendingService) {
    super(configService);

    taskService.onTaskStarted
      .pipe(untilDestroyed(this))
      .subscribe(task => this.onTaskQueued(task));

    taskService.onTaskChanged
    .pipe(
      untilDestroyed(this),
    )
    .subscribe(task => {
      switch (task?.status?.value) {
        case TaskStatusEnum.ended:
          this.onTaskReady(task);
          break;
        case TaskStatusEnum.canceled:
        case TaskStatusEnum.failed:
          this.stopPending(task);
          break;
        default:
          break;
      }
    });

  }

  ngOnDestroy(): void {
  }

  private async onTaskQueued(task: Task) {

    // Task  hiden. TODO: need enum
    if (task.visibility === 2) {
      return;
    }
    const pendingSubject = new BehaviorSubject<boolean>(true);

    if(!this.reportPendingState.get(task.id)) {
      this.pendingService.registerPendingState(pendingSubject);
      this.reportPendingState.set(task.id, pendingSubject);
    }
  }


  private async onTaskReady(task: Task) {
    const reportTask = this.registerdReportTasks.get(task.id);
    const rootTask = task.groupId ? this.registerdReportTasks.get(task.groupId) : null;

    if (rootTask || reportTask) {
        // Task not hidden hiden. TODO: need enum
        if (task.visibility !== 2 ||
            reportTask?.reportType === MmReportTypeEnum.writingPreview ||
            reportTask?.reportType === MmReportTypeEnum.writingPreviewEmailBody) {
          // dwonload file only if task was started from this client
          const file = await this.downloadReport(task);
          this.fileDownloadSubject.next({task, file});
        }
    }

    if (reportTask) {
      this.reportReadySubject.next(reportTask);
    }
    this.stopPending(task);
  }

  private stopPending(task: Task) {
    const pendingSubject = this.reportPendingState.get(task.id);
    if (pendingSubject) {
      pendingSubject.next(false);
      this.pendingService.removePendingState(pendingSubject);
    }
    const reportTask = this.registerdReportTasks.get(task.id);
    if (reportTask) {
      this.registerdReportTasks.delete(task.id);
    }
    this.finishSubject.next(task);
  }

  public registerReportTask(reportTask: MmReportTask) {
    if (!reportTask.taskId) {
      return;
    }
    this.registerdReportTasks.set(reportTask.taskId, reportTask);

  }

  public async downloadReport(task: Task): Promise<File> {

    try {

      if (!task.statusLog) {
        return null;
      }
      //TODO
      let fileUrl: string;
      task.statusLog.forEach(s => {
        if (s.file?.url) {
          fileUrl = s.file.url;
        }
      });
      if (!fileUrl) {
        return null;
      }

      const isIos = this.isIOSDevice();
      const isFirefox = window.navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
      const file = await this.taskDownloadService.downloadFile(fileUrl, {open: !isFirefox, preventBrowserDownload: isIos});
      this.notificationService.success(this.translateService.instant('MM.reporting.reportReady'));
      return file;

    } catch (er) {
      this.notificationService.error(this.translateService.instant('MM.reporting.reportDownloadError'));
    }
  }


  openMessageCenter() {
    this.appRightDrawerService.openContent('task-dashboard', this.appContainerOptions);

  }

  public isIOSDevice(): boolean {
    return !!navigator.userAgent && /iPad|iPhone|iPod/.test(navigator.userAgent);
  }
}
